// Step 5 — Pick a plan (US-027).
//
// Pixel-faithful to `prototypes_and_docs/ls-onboarding-v2.jsx:779-872`.
//
// Three plans: Discover / Scale ("POPULAIRE") / Agency. Stripe is the only
// source of truth for customer-facing amounts. The Pro plan is hidden in
// onboarding per `billing.md` — it shows up in Settings → Billing only.

import { ArrowRight, ChevronLeft, Gift } from 'lucide-react';

import { useBillingPrices } from '@barel/api-client/react';
import { useLocale, useT } from '@barel/i18n';
import { Banner, Button, OnboardingStep, Pill, RadioCardGroup } from '@barel/ui';

import type { OnboardingPlan } from '../lib/schemas';
import { findBillingPrice, formatBillingPrice } from '@/features/billing/prices';

interface PlanStepProps {
  plan: OnboardingPlan | null;
  onPlanChange: (plan: OnboardingPlan) => void;
  onBack: () => void;
  onContinue: () => void;
}

interface PlanCopy {
  id: OnboardingPlan;
  popular?: boolean;
}

const PLANS: PlanCopy[] = [{ id: 'discover' }, { id: 'scale', popular: true }, { id: 'agency' }];

export function Plan({ plan, onPlanChange, onBack, onContinue }: PlanStepProps) {
  const t = useT('onboarding');
  const locale = useLocale();
  const priceQuery = useBillingPrices();
  const prices = priceQuery.data?.prices ?? [];
  const selectedPrice = plan ? findBillingPrice(prices, plan, 'monthly') : undefined;
  const catalogIncomplete =
    !priceQuery.isLoading && PLANS.some(({ id }) => !findBillingPrice(prices, id, 'monthly'));

  const options = PLANS.map((planCopy) => {
    const price = findBillingPrice(prices, planCopy.id, 'monthly');
    return {
      value: planCopy.id,
      title: (
        <span className="flex items-center gap-2">
          <span className="text-sm font-bold">{t(`step5.plans.${planCopy.id}.name`)}</span>
          {planCopy.popular ? (
            <Pill tone="accent" size="xs" variant="soft" uppercase>
              {t('step5.popular')}
            </Pill>
          ) : null}
        </span>
      ),
      description: (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-secondary">{t(`step5.plans.${planCopy.id}.desc`)}</span>
          <div className="flex flex-wrap gap-1">
            {(['feature1', 'feature2'] as const).map((featKey) => (
              <span
                key={featKey}
                className="rounded-md border border-default bg-bg-surface px-2 py-0.5 text-2xs text-secondary"
              >
                {t(`step5.plans.${planCopy.id}.${featKey}`)}
              </span>
            ))}
          </div>
        </div>
      ),
      media: (
        <div className="flex flex-col items-end pl-2">
          <span className="text-xl font-bold leading-none text-primary">
            {price ? formatBillingPrice(price, locale) : '—'}
          </span>
          <span className="mt-1 text-2xs text-tertiary">{t('step5.perMonth')}</span>
        </div>
      ),
      disabled: !price,
    };
  });

  return (
    <OnboardingStep
      width="xl"
      eyebrow={t('step5.eyebrow')}
      title={t('step5.title')}
      description={t('step5.subtitle')}
      primary={
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          disabled={!plan || !selectedPrice}
          onClick={onContinue}
          iconRight={<ArrowRight aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step5-continue"
        >
          {t('step5.cta')}
        </Button>
      }
      secondary={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          iconLeft={<ChevronLeft aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step5-back"
        >
          {t('wizard.back')}
        </Button>
      }
    >
      <Banner
        tone="success"
        variant="gradient"
        icon={<Gift aria-hidden style={{ width: 18, height: 18 }} />}
        title={t('step5.trial.title')}
      >
        {t('step5.trial.body')}
      </Banner>

      {priceQuery.isError || catalogIncomplete ? (
        <Banner
          tone="warning"
          title={t('step5.prices.error')}
          action={
            <Button type="button" variant="ghost" size="sm" onClick={() => priceQuery.refetch()}>
              {t('step5.prices.retry')}
            </Button>
          }
          data-testid="onboarding-price-error"
        >
          {t('step5.prices.errorBody')}
        </Banner>
      ) : null}

      <RadioCardGroup
        name="onboarding-plan"
        aria-label={t('step5.title')}
        value={plan}
        onValueChange={onPlanChange}
        options={options}
        disabled={priceQuery.isLoading || priceQuery.isError}
      />
    </OnboardingStep>
  );
}
